// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📊 EXERCISE SESSIONS SERVICE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

import { supabase } from '../lib/supabase';
import type {
  ExerciseSession,
  ExerciseSessionWithDetails,
  CreateExerciseSessionParams,
  TrainingStatistics,
} from '../types';

// ============================================================================
// CREATE
// ============================================================================

export async function createExerciseSession(
  params: CreateExerciseSessionParams
): Promise<ExerciseSession> {
  const { data, error } = await supabase
    .from('exercise_sessions')
    .insert({
      player_id: params.player_id,
      exercise_id: params.exercise_id,
      score: params.score || null,
      score_data: params.score_data || null,
      personal_rating: params.personal_rating || null,
      notes: params.notes || null,
      duration_minutes: params.duration_minutes || null,
      training_task_id: params.training_task_id || null,
      virtual_training_session_id: params.virtual_training_session_id || null,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Complete a session
export async function completeExerciseSession(
  sessionId: number,
  completionData: {
    score?: number;
    score_data?: any;
    personal_rating?: number;
    notes?: string;
    duration_minutes?: number;
  }
): Promise<ExerciseSession> {
  const { data, error } = await supabase
    .from('exercise_sessions')
    .update({
      ...completionData,
      completed_at: new Date().toISOString(),
    })
    .eq('id', sessionId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ============================================================================
// READ
// ============================================================================

// Get single session
export async function getExerciseSession(id: number): Promise<ExerciseSessionWithDetails | null> {
  const { data, error } = await supabase
    .from('exercise_sessions')
    .select(`
      *,
      player:players(id, prename, name, profile_picture_url),
      exercise:exercises(id, title, category, difficulty)
    `)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
}

// Get player's sessions
export async function getPlayerExerciseSessions(
  playerId: number,
  limit?: number
): Promise<ExerciseSessionWithDetails[]> {
  let query = supabase
    .from('exercise_sessions')
    .select(`
      *,
      exercise:exercises(id, title, category, difficulty, scoring_type, scoring_config)
    `)
    .eq('player_id', playerId)
    .order('started_at', { ascending: false });

  if (limit) {
    query = query.limit(limit);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

// Get sessions for specific exercise
export async function getSessionsForExercise(
  exerciseId: number,
  playerId?: number
): Promise<ExerciseSessionWithDetails[]> {
  let query = supabase
    .from('exercise_sessions')
    .select(`
      *,
      player:players(id, prename, name, profile_picture_url)
    `)
    .eq('exercise_id', exerciseId)
    .order('started_at', { ascending: false });

  if (playerId) {
    query = query.eq('player_id', playerId);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

// Get sessions for training task
export async function getSessionsForTask(taskId: number): Promise<ExerciseSessionWithDetails[]> {
  const { data, error } = await supabase
    .from('exercise_sessions')
    .select(`
      *,
      player:players(id, prename, name, profile_picture_url),
      exercise:exercises(id, title, category, difficulty)
    `)
    .eq('training_task_id', taskId)
    .order('started_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// ============================================================================
// UPDATE
// ============================================================================

export async function updateExerciseSession(
  id: number,
  updates: Partial<CreateExerciseSessionParams>
): Promise<ExerciseSession> {
  const { data, error } = await supabase
    .from('exercise_sessions')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ============================================================================
// DELETE
// ============================================================================

export async function deleteExerciseSession(id: number): Promise<void> {
  const { error } = await supabase
    .from('exercise_sessions')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// ============================================================================
// STATISTICS
// ============================================================================

// Get player training statistics
export async function getPlayerTrainingStatistics(
  playerId: number
): Promise<TrainingStatistics> {
  // Get all sessions
  const { data: sessions } = await supabase
    .from('exercise_sessions')
    .select('*, exercise:exercises(category)')
    .eq('player_id', playerId);

  if (!sessions || sessions.length === 0) {
    return {
      total_sessions: 0,
      total_duration_minutes: 0,
      unique_exercises: 0,
      average_rating: 0,
      this_week_sessions: 0,
      this_week_duration: 0,
      improvement_trend: 'stable',
    };
  }

  // Calculate stats
  const totalSessions = sessions.length;
  const totalDuration = sessions.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
  const uniqueExercises = new Set(sessions.map(s => s.exercise_id)).size;
  
  const ratingsSum = sessions.reduce((sum, s) => sum + (s.personal_rating || 0), 0);
  const ratingsCount = sessions.filter(s => s.personal_rating).length;
  const averageRating = ratingsCount > 0 ? ratingsSum / ratingsCount : 0;

  // This week's stats
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  const thisWeekSessions = sessions.filter(
    s => new Date(s.started_at) >= oneWeekAgo
  );
  const thisWeekDuration = thisWeekSessions.reduce(
    (sum, s) => sum + (s.duration_minutes || 0),
    0
  );

  // Favorite category
  const categoryCount: Record<string, number> = {};
  sessions.forEach(s => {
    const category = s.exercise?.category;
    if (category) {
      categoryCount[category] = (categoryCount[category] || 0) + 1;
    }
  });
  const favoriteCategory = Object.entries(categoryCount).sort((a, b) => b[1] - a[1])[0]?.[0];

  // Improvement trend (compare last 2 weeks vs previous 2 weeks)
  const twoWeeksAgo = new Date();
  twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
  const fourWeeksAgo = new Date();
  fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);

  const lastTwoWeeksAvg = sessions
    .filter(s => new Date(s.started_at) >= twoWeeksAgo)
    .reduce((sum, s) => sum + (s.score || 0), 0) / sessions.filter(s => new Date(s.started_at) >= twoWeeksAgo).length || 0;

  const prevTwoWeeksAvg = sessions
    .filter(s => new Date(s.started_at) >= fourWeeksAgo && new Date(s.started_at) < twoWeeksAgo)
    .reduce((sum, s) => sum + (s.score || 0), 0) / sessions.filter(s => new Date(s.started_at) >= fourWeeksAgo && new Date(s.started_at) < twoWeeksAgo).length || 0;

  const improvementTrend = lastTwoWeeksAvg > prevTwoWeeksAvg ? 'up' : 
                          lastTwoWeeksAvg < prevTwoWeeksAvg ? 'down' : 'stable';

  return {
    total_sessions: totalSessions,
    total_duration_minutes: totalDuration,
    unique_exercises: uniqueExercises,
    average_rating: Math.round(averageRating * 10) / 10,
    this_week_sessions: thisWeekSessions.length,
    this_week_duration: thisWeekDuration,
    favorite_category: favoriteCategory as any,
    improvement_trend: improvementTrend,
  };
}

// Get progress for specific exercise
export async function getExerciseProgress(
  exerciseId: number,
  playerId: number
): Promise<{
  session_count: number;
  best_score: number | null;
  average_score: number | null;
  latest_session: ExerciseSession | null;
  improvement_percentage: number | null;
}> {
  const { data: sessions } = await supabase
    .from('exercise_sessions')
    .select('*')
    .eq('exercise_id', exerciseId)
    .eq('player_id', playerId)
    .order('started_at', { ascending: false });

  if (!sessions || sessions.length === 0) {
    return {
      session_count: 0,
      best_score: null,
      average_score: null,
      latest_session: null,
      improvement_percentage: null,
    };
  }

  const sessionCount = sessions.length;
  const scores = sessions.map(s => s.score).filter((s): s is number => s !== null);
  
  const bestScore = scores.length > 0 ? Math.max(...scores) : null;
  const averageScore = scores.length > 0 
    ? scores.reduce((sum, s) => sum + s, 0) / scores.length 
    : null;
  
  const latestSession = sessions[0];
  
  // Calculate improvement (first session vs latest)
  let improvementPercentage = null;
  if (scores.length >= 2) {
    const firstScore = scores[scores.length - 1];
    const latestScore = scores[0];
    improvementPercentage = ((latestScore - firstScore) / firstScore) * 100;
  }

  return {
    session_count: sessionCount,
    best_score: bestScore,
    average_score: averageScore ? Math.round(averageScore * 10) / 10 : null,
    latest_session: latestSession,
    improvement_percentage: improvementPercentage 
      ? Math.round(improvementPercentage * 10) / 10 
      : null,
  };
}

